import { useEffect, useRef, useState } from 'react';
import { type AsyncRateLimiterState, useAsyncRateLimiter } from '@tanstack/react-pacer';
import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { milliseconds } from 'date-fns';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';

import { type UserSessionVerifyResponse, sessionsV3VerifySession } from '@/api/generated';
import { logger } from '@/lib/logger';

type MutationParams = {
  token: string;
  dob: string;
};

// Rate limit: 3 attempts per 30 seconds
const RATE_LIMIT_WINDOW_MS = milliseconds({ seconds: 30 });
const RATE_LIMIT_ATTEMPTS = 3;

const fetcher = async (params: MutationParams) => {
  const { data } = await sessionsV3VerifySession({
    body: { dob: params.dob },
    query: { token: params.token } as any,
  });

  return data;
};

export function useSessionVerifyMutation(
  options?: UseMutationOptions<UserSessionVerifyResponse, Error, MutationParams>
) {
  const intervalRef = useRef<number | null>(null);
  const [, forceUpdate] = useState({});

  const asyncRateLimiter = useAsyncRateLimiter(
    fetcher,
    {
      onError: (error) => {
        logger.error('Failed to verify session', error);
      },
      limit: RATE_LIMIT_ATTEMPTS,
      window: RATE_LIMIT_WINDOW_MS,
      windowType: 'fixed',
      initialState: useSessionDobRateLimiterStore.getState().state ?? undefined,
    },
    // Selector to track state we need for UI updates
    (state) => state
  );

  useEffect(
    function persistState() {
      useSessionDobRateLimiterStore.getState().setState(asyncRateLimiter.state);
    },
    [asyncRateLimiter.state]
  );

  const remainingMs = asyncRateLimiter.getMsUntilNextWindow();

  useEffect(
    function resetExpiredRateLimiter() {
      if (asyncRateLimiter.state.isExceeded && remainingMs <= 0) {
        asyncRateLimiter.reset();
        useSessionDobRateLimiterStore.getState().resetState();
      }
    },
    [asyncRateLimiter, remainingMs]
  );

  const canSubmit = !asyncRateLimiter.state.isExceeded;

  useEffect(
    function forceCountdownRender() {
      if (!canSubmit) {
        intervalRef.current = window.setInterval(
          () => {
            // Force re-render to update countdown
            forceUpdate({});
          },
          milliseconds({ seconds: 1 })
        );
      } else {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      return () => {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
        }
      };
    },
    [canSubmit]
  );

  const mutation = useMutation({
    mutationFn: async (data) => {
      const result = await asyncRateLimiter.maybeExecute(data);

      if (result === undefined) {
        if (asyncRateLimiter.state.isExceeded) {
          throw new Error('Rate limit exceeded');
        }
        throw new Error('Verification failed. Please check your information and try again.');
      }

      return result;
    },
    retry: false,
    ...options,
  });

  return {
    ...mutation,
    remainingMs,
    canSubmit,
  };
}

type RateLimiterState = AsyncRateLimiterState<
  (data: MutationParams) => Promise<UserSessionVerifyResponse>
>;

interface SessionDobRateLimiterStore {
  state: RateLimiterState | null;
  setState: (state: RateLimiterState) => void;
  resetState: () => void;
}

const useSessionDobRateLimiterStore = create<SessionDobRateLimiterStore>()(
  devtools(
    persist(
      (set) => ({
        state: null,
        setState: (state) => set({ state }),
        resetState: () => set({ state: null }),
      }),
      {
        name: 'session-dob-rate-limiter-storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    ),
    {
      name: 'SessionDobRateLimiter',
      enabled: import.meta.env.MODE !== 'test',
    }
  )
);
